/**
 * DataPathBuilder Service
 *
 * Builds and parses data paths for screen user inputs.
 * Single source of truth for data path formats.
 *
 * Features:
 * - Build data paths for any screen type
 * - Extract all data inputs from a screen (with full paths)
 * - Parse paths back to components (screenId, inputKey)
 * - Check if a screen type has user input
 *
 * @module screen-utils
 */

import {
  DATA_PATH_TEMPLATES,
  SCREEN_TYPE_TO_CATEGORY,
  CUSTOM_INPUT_PATH_REGEX,
  ARRAY_DATA_SCREEN_TYPES,
  NUMBER_DATA_SCREEN_TYPES,
} from '../constants/data-path-constants';
import type {
  DataPathCategory,
  DataInputInfo,
  ParsedDataPath,
} from '../types/data-path.types';

/**
 * Service for building and parsing data paths for screen user inputs.
 */
export class DataPathBuilder {
  /**
   * Build data path for a screen.
   * For CustomScreen, returns base path (screenId only) since it has multiple inputs.
   *
   * @param screenId - The screen ID
   * @param screenType - The screen type
   * @returns Full data path (e.g., "screen1.list.checked")
   */
  buildDataPath(screenId: string, screenType: string): string {
    const category = SCREEN_TYPE_TO_CATEGORY[screenType];

    if (!category) {
      return screenId;
    }

    // CustomScreen: return just screenId, full paths are per-input
    if (category === 'customInput') {
      return screenId;
    }

    return `${screenId}.${DATA_PATH_TEMPLATES[category]}`;
  }

  /**
   * Build full data path for a CustomScreen input.
   *
   * @param screenId - The screen ID
   * @param inputKey - The input key (from struct.values)
   * @returns Full data path (e.g., "screen2104.input['has_work_at_office'].value")
   */
  buildCustomInputPath(screenId: string, inputKey: string): string {
    const template = DATA_PATH_TEMPLATES.customInput;
    return `${screenId}.${template.replace('%s', inputKey)}`;
  }

  /**
   * Extract all data inputs from a screen.
   *
   * For CustomScreen: returns array with each input having full dataPath.
   * For other screens: returns single item with screen's dataPath.
   *
   * @param screenId - The screen ID
   * @param screenName - Display name for the screen
   * @param screenType - The screen type
   * @param struct - The screen struct containing input data
   * @returns Array of data input info with full paths
   */
  extractDataInputs(
    screenId: string,
    screenName: string,
    screenType: string,
    struct: unknown
  ): DataInputInfo[] {
    const category = SCREEN_TYPE_TO_CATEGORY[screenType];

    if (!category) {
      return [];
    }

    // CustomScreen: extract each input from struct.values
    if (category === 'customInput') {
      return this.extractCustomScreenInputs(screenId, screenName, struct);
    }

    // Other screens: single data input
    return [{
      key: screenId,
      title: screenName,
      type: this.getDataTypeForScreenType(screenType, struct),
      dataPath: this.buildDataPath(screenId, screenType),
    }];
  }

  /**
   * Parse a data path back to its components.
   *
   * @param dataPath - Full data path to parse
   * @returns Parsed components or null if invalid
   */
  parsePath(dataPath: string): ParsedDataPath | null {
    if (!dataPath) {
      return null;
    }

    // Try CustomScreen input format first: screenId.input['key'].value
    const customMatch = dataPath.match(CUSTOM_INPUT_PATH_REGEX);
    if (customMatch) {
      return {
        screenId: customMatch[1],
        category: 'customInput',
        inputKey: customMatch[2],
      };
    }

    // Try standard formats
    const parts = dataPath.split('.');
    if (parts.length < 2) {
      return { screenId: dataPath, category: 'list' }; // Default
    }

    const screenId = parts[0];
    const pathSuffix = parts.slice(1).join('.');

    // Match against known templates
    for (const [cat, template] of Object.entries(DATA_PATH_TEMPLATES)) {
      if (cat !== 'customInput' && pathSuffix === template) {
        return { screenId, category: cat as DataPathCategory };
      }
    }

    // Default to list category
    return { screenId, category: 'list' };
  }

  /**
   * Check if a screen type has user input.
   *
   * @param screenType - The screen type to check
   * @returns True if the screen type collects user input
   */
  hasUserInput(screenType: string): boolean {
    return screenType in SCREEN_TYPE_TO_CATEGORY;
  }

  /**
   * Get path category for a screen type.
   *
   * @param screenType - The screen type
   * @returns Category or null if not a user input screen
   */
  getCategory(screenType: string): DataPathCategory | null {
    return SCREEN_TYPE_TO_CATEGORY[screenType] || null;
  }

  /**
   * Get the data type for a screen type.
   *
   * @param screenType - The screen type
   * @param struct - Optional struct for field type detection
   * @returns Data type string ('array', 'number', 'string')
   */
  getDataTypeForScreenType(screenType: string, struct?: unknown): string {
    // Multi-select screens produce array
    if (ARRAY_DATA_SCREEN_TYPES.includes(screenType)) {
      return 'array';
    }

    // Single-select screens produce number (index)
    if (NUMBER_DATA_SCREEN_TYPES.includes(screenType)) {
      return 'number';
    }

    // Field screens - check field.type
    if (screenType === 'ScreenTitleSubtitleField' && struct) {
      const s = struct as Record<string, unknown>;
      const field = s?.field as Record<string, unknown> | undefined;
      const fieldType = field?.type as string;
      if (fieldType === 'int' || fieldType === 'double') {
        return 'number';
      }
      return 'string';
    }

    // Picker and CustomScreen default to string
    return 'string';
  }

  // ========== Private Methods ==========

  /**
   * Extract inputs from CustomScreen struct.values.
   * Each input becomes a separate DataInputInfo with full dataPath.
   */
  private extractCustomScreenInputs(
    screenId: string,
    screenName: string,
    struct: unknown
  ): DataInputInfo[] {
    const s = struct as Record<string, unknown> | undefined;
    const values = s?.values as Record<string, unknown> | undefined;

    if (!values || typeof values !== 'object') {
      return [];
    }

    // values is an object: { "input_key": { name, type, value } }
    return Object.entries(values).map(([key, valueObj]) => {
      const record = valueObj as Record<string, unknown>;
      const name = (record.name as string) || key;
      const type = (record.type as string) || 'string';

      return {
        key: name,
        title: `${screenName} → ${name}`,
        type,
        dataPath: this.buildCustomInputPath(screenId, name),
      };
    }).filter(input => input.key);
  }
}

/** Singleton instance */
export const dataPathBuilder = new DataPathBuilder();
